import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FilterDropDownType, UserRoles } from "@/utils/utils";
import CustomFilterDropdown from "@/components/CustomFilterDropdown/CustomFilterDropdown";
import "./ConnectionGrid.css";
import { Tooltip, Typography, Box, TextField } from "@mui/material";
import {
  selectConnectionSummaryRecords,
  fetchConnectionSummaries,
  selectConnectionSummaryRecordLoaded,
  setConnectionSummaryFilterTypes,
  setConnectionSummaryFilterStatus,
  selectConnectionSummaryFilterTypes,
  selectConnectionSummaryFilterStatus,
  connEndpointStatus,
  connectionTypes,
  OperationProcessState,
  setSelectedConnection,
  selectTriggerRefreshForConnectionSummary,
  setEditConnectionOpen,
  setEditConnectionId,
} from "@/features/connections/integrationSlice";
import ShepTooltip from "@/components/GenericComponents/ShepTooltip";
import ShepHorizontalMenu from "@/components/GenericComponents/ShepHorizontalMenu";
import ThreeDotSettingMenu from "./ThreeDotSettingMenu";
import _ from "lodash";
import { rightColumn } from "@/components/Homepage/Map/ArtifactMapDefinitions";
import AddConnectionPopup from "@/components/Integrations/ManageConnection/AddConnection/AddConnectionPopup";
import axios from "../../features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import ShepTable from "@/components/GenericComponents/ShepTable";
import { selectIntegrationDateFilter } from "@/features/dateFilterSlice";
import { fetchUserRole, selectUserRole } from "@/features/auth/userSlice";

const getType = (type) => {
  return loadLabel(type, connectionTypes);
};

const loadLabel = (key, collection) => {
  if (!collection) return "";
  const items = collection.filter((x) => x.key === key);
  if (items && items.length) {
    return items[0].label;
  }
};
const getStatusClass = (status) => {
  switch (status) {
    case OperationProcessState.Completed:
      return "connection-summary-status-success";
    case OperationProcessState.CompletedWithWarning:
      return "connection-summary-status-success";
    case null:
      return "";
    default:
      return "connection-summary-status-error";
  }
};

const ConnectionGrid = (props) => {
  const dispatch = useDispatch();
  const [referenceFilter, setReferenceFilter] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [addConnectionOpen, setAddConnectionOpen] = useState(false);
  const statusFilter = useSelector(selectConnectionSummaryFilterStatus);
  const typeFilter = useSelector(selectConnectionSummaryFilterTypes);
  const { startDate, endDate } = useSelector(selectIntegrationDateFilter) || {};
  const recordLoaded = useSelector(selectConnectionSummaryRecordLoaded);
  const rows = useSelector(selectConnectionSummaryRecords);
  const triggerRefresh = useSelector(selectTriggerRefreshForConnectionSummary);
  const userRole = useSelector(selectUserRole);
  const columns = [
    {
      field: "DisplayName",
      headerName: "Connection",
      width: 230,
    },
    {
      field: "ConnectionType",
      headerName: "Type",
      width: 180,
      renderCell: (params) => {
        return getType(params.value);
      },
    },
    {
      field: "EndpointName",
      headerName: "Endpoint",
      width: 220,
    },
    {
      field: "Status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        const statusClass = getStatusClass(params.value);
        return (
          <ShepTooltip
            title={
              params.value < OperationProcessState.FailedWithError
                ? loadLabel(OperationProcessState.Completed, connEndpointStatus)
                : loadLabel(
                    OperationProcessState.FailedWithError,
                    connEndpointStatus,
                  )
            }
          >
            <div className={statusClass}></div>
          </ShepTooltip>
        );
      },
    },
    {
      field: "TotalAttempts",
      headerName: "Error Rate",
      width: 320,
      renderCell: (params) => {
        let message = "";
        const failed = params.row.FailedAttempts;
        const total = params.value;
        if (total && total > 0) {
          message =
            failed +
            " of " +
            total +
            " (" +
            Math.round((failed * 100) / total) +
            "%) attempts  failed   ";
        }
        return <Typography>{message}</Typography>;
      },
    },
    {
      field: "SchemaCollectionName",
      headerName: "Schema Library ID",
      width: 300,
    },
    {
      field: "LogTimeUTC",
      headerName: "Last Update",
      width: 200,
      flex: 1,
      type: "date",
      valueGetter: (params) => (params.value ? new Date(params.value) : null),
      sortComparator: (v1, v2) => v2 - v1, // makes sort asc return newest first
      renderCell: (params) => {
        return <Typography>{params.row.LastUpdated}</Typography>;
      },
    },
    {
      field: "threeDotMenu",
      type: "actions",
      sortable: false,
      getActions: (params) => {
        return [
          <ThreeDotSettingMenu id={params.row.id} selectedRow={params.row} />,
        ];
      },
    },
  ];

  useEffect(() => {
    prepFilters();
    dispatch(fetchUserRole());
  }, []);

  const getStatusFilterString = (args) => {
    const status = statusFilter;

    return status
      ? JSON.stringify(status.filter((x) => x.isChecked).map((x) => x.key))
      : "";
  };

  const getTypeFilterString = (args) => {
    const types = typeFilter;

    return types
      ? JSON.stringify(types.filter((x) => x.isChecked).map((x) => x.key))
      : "";
  };

  const prevDates = useRef({ startDate, endDate });

  useEffect(() => {
    if (!startDate || !endDate) return; //block until ready
    const params = {
      statusFilterJson: getStatusFilterString(),
      typeFilterJson: getTypeFilterString(),
      search: referenceFilter,
      displayType: props.displayType,
      startDate: startDate,
      endDate: endDate,
    };
    const dateChanged =
      prevDates.current.startDate !== startDate ||
      prevDates.current.endDate !== endDate;

    if (dateChanged) {
      dispatch(fetchConnectionSummaries(params));
    } else {
      debouncedGetConnectionSummary(params);
    }
    prevDates.current = { startDate, endDate };
  }, [
    startDate,
    endDate,
    typeFilter,
    statusFilter,
    referenceFilter,
    triggerRefresh,
  ]);

  const getConnectionsFromServer = (params) => {
    dispatch(fetchConnectionSummaries(params));
  };
  const debouncedGetConnectionSummary = useCallback(
    _.debounce(getConnectionsFromServer, 1000),
    [],
  );
  const prepFilters = () => {
    dispatch(setConnectionSummaryFilterTypes({ value: connectionTypes }));
    dispatch(setConnectionSummaryFilterStatus({ value: connEndpointStatus }));
  };
  const schema = [
    {
      id: 0,
      label: "Status",
      type: FilterDropDownType.CheckboxList,
      cascading: true,
      includingAllCheckBox: true,
      childLabel: "Select Status",
      setValue: setConnectionSummaryFilterStatus,
      readVal: selectConnectionSummaryFilterStatus,
      labelKey: "label",
      uniqueKey: "key",
    },
    {
      id: 1,
      label: "Connection Type",
      type: FilterDropDownType.CheckboxList,
      cascading: true,
      includingAllCheckBox: true,
      childLabel: "Select Connection Type",
      setValue: setConnectionSummaryFilterTypes,
      readVal: selectConnectionSummaryFilterTypes,
      labelKey: "label",
      uniqueKey: "key",
    },
  ];

  const connectionActions = (
    userRole === UserRoles.SuperAdmin
      ? [
          {
            label: "Add Connection",
            onClick: () => {
              setAddConnectionOpen(true);
            },
          },
        ]
      : []
  ).concat([
    {
      label: "Delete",
      onClick: () => handleConnectionDelete(null, true),
      variant: "danger",
    },
  ]);

  const handleRowClick = (ids) => {
    const selectedIDs = new Set(ids);
    const selectedRowData = rows.filter((row) => selectedIDs.has(row.id));
    setSelectedRows(selectedRowData);
  };

  const handleConnectionDelete = (params, batch = false) => {
    if (!batch) {
      deleteRows([params.row.ConnectionId]);
    } else {
      if (!selectedRows?.length) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Please select at least one connection.",
          }),
        );
        return;
      }
      let selectedRowsToDelete = selectedRows.map((row) => row.ConnectionId);
      deleteRows(selectedRowsToDelete);

      // heap.io
      window.heap.track("Connection Manager - Delete Connection");
    }
  };

  const deleteRows = (selectedRowsToDelete) => {
    axios
      .delete("/connections/delete-connection-without-connection-pairs", {
        params: {
          connectionIds: JSON.stringify(selectedRowsToDelete),
        },
      })
      .then((response) => {
        if (response?.status === 201) {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "single",
              message: response?.data?.Message,
            }),
          );
        } else if (response?.data) {
          dispatch(fetchConnectionSummaries());
          dispatch(
            createAlert({
              status: "Success",
              alertType: "single",
              message: "Connection Deleted!",
            }),
          );
        } else {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "single",
              message: "Error deleting the connection",
            }),
          );
        }
      })
      .catch((error) => {
        console.error(error);
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Something went wrong, please try again!",
          }),
        );
      });
  };

  const handleTextChange = (event, data) => {
    const referenceNumber = event.target.value;
    setReferenceFilter(referenceNumber);
  };

  const [sortModel, setSortModel] = useState([
    { field: "updatedTime", sort: "asc" },
  ]);

  // when no sorting is applied, reset to default sorted by connection name asc
  const handleSortModelChange = (newModel) => {
    setSortModel(
      newModel.length == 0 ? [{ field: "DisplayName", sort: "asc" }] : newModel,
    );
  };

  return (
    <Box sx={{ display: "block", flexDirection: "column" }}>
      <Box style={{ display: "flex", paddingBottom: 5 }}>
        <CustomFilterDropdown schemas={schema} />
        <TextField
          placeholder={props.searchText}
          value={referenceFilter}
          onChange={handleTextChange}
          sx={{
            paddingLeft: "10px",
            width: "450px",
            "&:hover fieldset": {
              backgroundColor: "#ffffff",
            },
            "&.MuiFormControl-root .MuiInputBase-root": {
              borderRadius: "5px",
            },
            "&:hover fieldset": {
              color: "#000000",
            },
          }}
        />
        {props.hasGridActions && (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <ShepHorizontalMenu actions={connectionActions} />
          </div>
        )}
      </Box>
      <Box
        className="content-box"
        sx={{ display: "flex", flexDirection: "row" }}
      >
        <Box sx={{ width: "100%" }}>
          <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
            <ShepTable
              tableTitle={props.title}
              isDataLoaded={recordLoaded}
              dataRows={rows}
              columnTitles={columns}
              checkboxesEnabled={props.canSelect}
              selectionDisabled={!props.canSelect}
              handleSelectedRowChange={handleRowClick}
              columnVisibilityModel={props.columnVisibilityModel}
            />
          </Box>
        </Box>
      </Box>
      {addConnectionOpen && (
        <AddConnectionPopup
          open={addConnectionOpen}
          onClose={() => {
            setAddConnectionOpen(false);
            setEditConnectionOpen(false);
            dispatch(setEditConnectionOpen(false));
            dispatch(setSelectedConnection(false));
            dispatch(setEditConnectionId(false));
          }}
        />
      )}
    </Box>
  );
};

export default ConnectionGrid;
